import { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Group, Menu, ScrollArea, SegmentedControl, Skeleton, rem } from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';

import { AdminSection } from '@/entities/Navbar/AdminSection.tsx';
import { ModeratorSection } from '@/entities/Navbar/ModeratorSection.tsx';
import { ReportsSection } from '@/entities/Navbar/ReportsSection.tsx';
import { ScudMappingSection } from '@/entities/Navbar/ScudMappingSection.tsx';

import { servicesApi } from '@/shared/api/services.ts';
import { USER_ROUTES } from '@/shared/constant/app';
import { FOOD_SERVICE_URL } from '@/shared/constant/services.ts';
import { useScudMappingPermissions } from '@/shared/hooks/useScudMappingPermissions';

import { IconChevronDown } from '@tabler/icons-react';

import classes from './Navbar.module.css';

export const Navbar = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { admin, moderator, isReportsAvailable } = useContext(AppContext);
	const { isAvailable: isScudMappingAvailable } = useScudMappingPermissions();

	const { data: isAvailableFoodService = false, isPending: isAvailableFoodServicePending } =
		useQuery({
			queryKey: ['food_service'],
			queryFn: servicesApi.isAvailableFoodService,
		});

	const checkAvailableServices = () => {
		const addServices = (initRoutes: Array<{ label: string; value: string }>) =>
			initRoutes.slice(0, -2).concat([FOOD_SERVICE_URL]).concat(initRoutes.slice(-2));

		return isAvailableFoodService ? addServices(USER_ROUTES) : USER_ROUTES;
	};

	const administrationMenu = useMemo(() => {
		if (admin || moderator || isReportsAvailable || isScudMappingAvailable) {
			return (
				<Menu
					transitionProps={{ transition: 'pop-top-right' }}
					position="top-end"
					width={isScudMappingAvailable ? 290 : 220}
					withinPortal
				>
					<Menu.Target>
						<Button
							variant="transparent"
							color="white"
							c="black"
							size="compact-md"
							radius={10}
							rightSection={
								<IconChevronDown style={{ width: rem(18), height: rem(18) }} stroke={1.5} />
							}
							pr={12}
						>
							Администрирование
						</Button>
					</Menu.Target>
					<Menu.Dropdown>
						{(admin || moderator) && <ModeratorSection />}
						{admin && <AdminSection />}
						{(admin || isReportsAvailable) && <ReportsSection />}
						{isScudMappingAvailable && <ScudMappingSection />}
					</Menu.Dropdown>
				</Menu>
			);
		}

		return <></>;
	}, [admin, moderator, isReportsAvailable, isScudMappingAvailable]);

	return (
		<ScrollArea w={'fit-content'} scrollbarSize={2}>
			<Group gap={8}>
				<Skeleton visible={isAvailableFoodServicePending}>
					<SegmentedControl
						classNames={classes}
						value={pathname}
						data={checkAvailableServices()}
						onChange={(path) =>
							path.includes('http') ? window.open(path, '_blank') : navigate(path)
						}
						withItemsBorders={false}
					/>
					{administrationMenu}
				</Skeleton>
			</Group>
		</ScrollArea>
	);
};
